import type {
  Configuration,
  CreateMPAPolicyRequest,
  CreateMPARequestReviewRequest,
  ListMPARequestsRequest,
  ListMyMPARequestsRequest,
  MPAOperationType,
  MPARequest,
  UpdateMPAPolicyRequest,
} from '@repo/api-gw-sdk';
import { ActionApprovalsApi } from '@repo/api-gw-sdk';
import type { SWRConfiguration } from 'swr';

import type { HttpClient } from './httpClient';

const baseUrl = '/action-approvals';

export const mpa = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const actionApprovalsApi = new ActionApprovalsApi(config);

  return {
    operations: {
      useList: () =>
        httpClient.execute(
          actionApprovalsApi.listActionApprovalOperations.bind(
            actionApprovalsApi
          ),
          baseUrl,
          'operations'
        ),
    },
    policies: {
      useList: (operation?: MPAOperationType) =>
        httpClient.execute(
          actionApprovalsApi.listActionApprovalPolicies.bind(
            actionApprovalsApi
          ),
          baseUrl,
          'policies',
          currentProjectId,
          operation
        ),
      useHasPolicies: () => {
        const { data, ...rest } = httpClient.execute(
          actionApprovalsApi.listActionApprovalPolicies.bind(
            actionApprovalsApi
          ),
          baseUrl,
          'hasPolicies',
          currentProjectId,
          undefined,
          undefined,
          1
        );
        return { ...rest, data: (data?.policies?.length ?? 0) > 0 };
      },
      create: (body: CreateMPAPolicyRequest) =>
        actionApprovalsApi.createActionApprovalPolicy(currentProjectId, body),
      update: (mpaPolicyId: string, body: UpdateMPAPolicyRequest) =>
        actionApprovalsApi.updateActionApprovalPolicy(
          currentProjectId,
          mpaPolicyId,
          body
        ),
      delete: (mpaPolicyId: string) =>
        actionApprovalsApi.deleteActionApprovalPolicy(
          currentProjectId,
          mpaPolicyId
        ),
    },
    myRequests: {
      useList: (
        body?: ListMyMPARequestsRequest,
        pageToken?: string,
        pageSize?: number,
        swrOptions?: SWRConfiguration,
        enabled = true
      ) =>
        httpClient.executeWithConfig(
          actionApprovalsApi.listMyActionApprovalRequests.bind(
            actionApprovalsApi
          ),
          enabled ? baseUrl : null,
          'my-requests',
          swrOptions ?? {},
          currentProjectId,
          pageToken,
          pageSize,
          body
        ),
      useGet: (requestId: string | null) =>
        httpClient.execute(
          actionApprovalsApi.getMyActionApprovalRequest.bind(
            actionApprovalsApi
          ),
          requestId ? baseUrl : null,
          `my-requests/${requestId}`,
          currentProjectId,
          requestId ?? ''
        ),
      cancel: (requestId: string) =>
        actionApprovalsApi.cancelActionApprovalRequest(
          currentProjectId,
          requestId
        ),
      // Replay the original API call captured on the action approval request, tagged with
      // the request id so the backend MPA middleware transitions it from
      // APPROVED to EXECUTED and lets the original handler run.
      apply: (request: MPARequest) => {
        if (!request.requestMethod || !request.requestPath) {
          throw new Error('action approval request is missing method or path');
        }
        const headers: Record<string, string> = {
          'X-Action-Approval-Request-Id': request.id,
        };
        if (request.requestBody) {
          headers['Content-Type'] = 'application/json';
        }
        return httpClient.request(request.requestMethod, request.requestPath, {
          body: request.requestBody ?? undefined,
          headers,
        });
      },
    },
    requests: {
      useList: (
        body?: ListMPARequestsRequest,
        pageToken?: string,
        pageSize?: number,
        swrOptions?: SWRConfiguration,
        enabled = true
      ) =>
        httpClient.executeWithConfig(
          actionApprovalsApi.listActionApprovalRequests.bind(
            actionApprovalsApi
          ),
          enabled ? baseUrl : null,
          'requests',
          swrOptions ?? {},
          currentProjectId,
          pageToken,
          pageSize,
          body
        ),
      useGet: (requestId: string | null) =>
        httpClient.execute(
          actionApprovalsApi.getActionApprovalRequest.bind(actionApprovalsApi),
          requestId ? baseUrl : null,
          `requests/${requestId}`,
          currentProjectId,
          requestId ?? ''
        ),
      review: (requestId: string, body: CreateMPARequestReviewRequest) =>
        actionApprovalsApi.createActionApprovalRequestReview(
          currentProjectId,
          requestId,
          body
        ),
    },
  };
};
